import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { formatDate, formatCurrency, getExpiryStatus } from "@/lib/utils-date";
import { formatKg, parsePackLabelKg, formatEstimatedPieces } from "@/lib/format";
import MarginPill from "./MarginPill";
import SetPricePill from "./SetPricePill";
import LotsList from "./LotsList";
import { usePerm } from "@/context/AuthContext";

export default function ProductCard({ product, onSell, onEdit, onDelete, onChanged }) {
  const canProductsWrite = usePerm("products_write");
  const canSalesWrite = usePerm("sales_write");
  const st = getExpiryStatus(product.expiry_date);
  const hasPrice = Number(product.price_per_kg) > 0 || Number(product.price) > 0;
  // Fonte di verità coerente con sale_unit (fallback: presenza di stock_kg).
  const isWeightUnit = product.sale_unit === "weight" || (product.stock_kg !== null && product.stock_kg !== undefined);
  const isOutOfStock = isWeightUnit ? Number(product.stock_kg) <= 0 : Number(product.stock) <= 0;
  // Nasconde stati neutri/positivi quando esaurito. Scaduto/Scade oggi (badge-expired) restano visibili.
  const showExpiryBadge = !(isOutOfStock && st.cls !== "badge-expired");
  return (
    <div data-testid={`product-card-${product.id}`} className="mag-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-[#1A1C1A] text-base leading-tight">{product.name}</span>
            {product.is_fresh && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-1.5 py-0.5 rounded flex-shrink-0">
                Fresco
              </span>
            )}
          </div>
          <div className="text-xs text-[#8E918C]">{product.category}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-base font-semibold text-[#1A1C1A] font-mono">
            {hasPrice ? (
              product.price_per_kg
                ? `${formatCurrency(product.price_per_kg)}/kg`
                : formatCurrency(product.price)
            ) : (
              <SetPricePill product={product} onClick={onEdit} size="sm" />
            )}
          </div>
          <div className="flex items-center justify-end gap-1.5 mt-0.5">
            <MarginPill product={product} size="xs" />
            {product.stock_kg !== null && product.stock_kg !== undefined ? (
              <div className={`text-xs font-mono ${product.stock_kg <= 0 ? "text-[#C65D47] font-semibold" : product.stock_kg <= 0.5 ? "text-[#8B6518]" : "text-[#5C5F5A]"}`}>
                {product.stock_kg <= 0 ? "esaurito" : formatKg(product.stock_kg)}
              </div>
            ) : (
              <div className={`text-xs font-mono ${
                product.min_stock !== null && product.min_stock !== undefined && product.stock <= product.min_stock
                  ? "text-[#C65D47] font-semibold"
                  : product.stock <= 3
                  ? "text-[#C65D47] font-semibold"
                  : "text-[#5C5F5A]"
              }`}>
                {product.stock} u.{product.min_stock !== null && product.min_stock !== undefined ? <span className="text-[#8E918C] font-normal">/min {product.min_stock}</span> : null}
                {isOutOfStock && <span className="text-[#C65D47] font-semibold"> esaurito</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {product.sale_unit === "weight" ? (
          Number(product.stock_kg) > 0 && (
            <>
              <span className="font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] px-2 py-0.5 rounded-md">
                {formatKg(product.stock_kg)}
              </span>
              {(() => {
                // Stima pezzi: visualizzazione only, mai salvata, nulla se
                // pezzatura in L / mancante / non parsabile come peso.
                const est = formatEstimatedPieces(
                  product.stock_kg,
                  parsePackLabelKg(product.weight),
                );
                return est ? (
                  <span
                    data-testid={`product-estimated-pieces-${product.id}`}
                    className="font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-dashed border-[#EAE7E1] px-2 py-0.5 rounded-md"
                    title="Stima calcolata da giacenza / pezzatura"
                  >
                    {est}
                  </span>
                ) : null;
              })()}
            </>
          )
        ) : (
          product.weight && (
            <span className="font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] px-2 py-0.5 rounded-md">
              {product.weight}
            </span>
          )
        )}
        {product.batch && (
          <span className="font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] px-2 py-0.5 rounded-md">
            Lotto {product.batch}
          </span>
        )}
        {showExpiryBadge && (
          <span className={`${st.cls} font-semibold px-2 py-0.5 rounded-md`}>{st.label}</span>
        )}
        {product.expiry_date && (
          <span className="text-[#8E918C] text-[11px]">{formatDate(product.expiry_date)}</span>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-[#EAE7E1]">
        {canSalesWrite && (
        <Button
          data-testid={`sell-product-mobile-${product.id}`}
          variant="outline"
          size="sm"
          onClick={onSell}
          className="flex-1 border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA] h-9"
        >
          Vendi
        </Button>
        )}
        {canProductsWrite && (
        <Button
          data-testid={`edit-product-mobile-${product.id}`}
          variant="ghost"
          size="icon"
          onClick={onEdit}
          className="h-9 w-9 hover:bg-[#F4F1ED]"
        >
          <Pencil size={14} />
        </Button>
        )}
        {canProductsWrite && (
        <Button
          data-testid={`delete-product-mobile-${product.id}`}
          variant="ghost"
          size="icon"
          onClick={onDelete}
          className="h-9 w-9 text-[#C65D47] hover:bg-[#FCEAE8]"
        >
          <Trash2 size={14} />
        </Button>
        )}
      </div>
      <LotsList product={product} onChanged={onChanged} />
    </div>
  );
}
